// =====================================================
// 📁 POSCreditNoteProductListItem.tsx
// =====================================================

import { useEffect, useState } from 'react';

import { motion } from 'framer-motion';

import POSActionButton from '../shared/POSActionButton';

import POSCreditNoteOverlay from '../shared/POSCreditNoteOverlay';

import ProductPanelService from '../../../../../services/product/ProductPanelService';

import POSCreditQuantityModal from '../modal/POSCreditQuantityModal';

import { useSale } from '../../../../../hooks/sale/useSale';

import { useSaleDispatcher } from '../../../../../hooks/sale/useSaleDispatcher';

interface Props {
  product: any;
}

const POSCreditNoteProductListItem: React.FC<Props> = ({ product }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const { form } = useSale();

  const { addProduct } = useSaleDispatcher();

  // =====================================================
  // 🔹 States
  // =====================================================
  const [showModal, setShowModal] = useState(false);

  // =====================================================
  // 🔹 States
  // =====================================================
  const [imageUrl, setImageUrl] = useState('');

  // =====================================================
  // 🔹 Effects
  // =====================================================
  useEffect(() => {
    const loadImage = async () => {
      if (!product.imagePath) return;

      try {
        const url = await ProductPanelService.getProductImage(
          product.imagePath
        );

        if (url) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('❌ Error loading product image:', error);
      }
    };

    loadImage();
  }, [product.imagePath]);


  const soldQuantity = Number(product.soldQuantity ?? product.quantity ?? 0);

  const returnedQuantity = Number(product.returnedQuantity ?? 0);

  const remainingQuantity = Number(
    product.remainingQuantity ?? soldQuantity - returnedQuantity
  );

  const canBeReturned =
    product.canBeReturned !== false && remainingQuantity > 0;

  // =====================================================
  // 🔹 Confirm Return
  // =====================================================
  const handleConfirmReturn = (quantity: number) => {
    if (!form?.id) return;

    addProduct({
      saleId: form.id,

      productId: product.productId,

      quantity,

      unitPrice: Number(product.unitPrice || 0),

      taxType: 'TAXED',

      creditFiscalId: product.creditFiscalId,
    });

    setShowModal(false);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <>
      <motion.div
        layout
        className="
          hover:bg-gray-1/40

          relative
          flex

          flex-col
          border-b

          border-stroke

          transition-all

          dark:border-strokedark
          dark:hover:bg-white/[0.02]

          lg:flex-row
          lg:items-center
          lg:gap-4
        "
      >
        {/* 🔹 Overlay */}
        {!canBeReturned && <POSCreditNoteOverlay />}

        {/* ===================================================== */}
        {/* 🔹 Mobile */}
        {/* ===================================================== */}
        <div
          className="
            flex
            flex-col
            gap-4

            p-4

            lg:hidden
          "
        >
          {/* 🔹 Top */}
          <div className="flex gap-3">
            {/* 🔹 Image */}
            <div
              className="
                flex
                h-16
                w-16
                shrink-0
                items-center
                justify-center

                overflow-hidden

                rounded-xl
                border
                border-stroke

                bg-whiten

                dark:border-strokedark
                dark:bg-boxdark
              "
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={product.productName}
                  className="
                    h-full
                    w-full
                    object-cover
                  "
                />
              ) : (
                <span
                  className="
                    text-[10px]
                    font-medium
                    text-body
                  "
                >
                  IMG
                </span>
              )}
            </div>

            {/* 🔹 Info */}
            <div className="min-w-0 flex-1">
              <h3
                className="
                  line-clamp-2

                  text-sm
                  font-semibold
                  text-black

                  dark:text-white
                "
              >
                {product.productName}
              </h3>

              <div
                className="
                  mt-2

                  flex
                  flex-wrap
                  items-center
                  gap-2
                "
              >
                <span
                  className="
                    rounded-full
                    bg-primary/10

                    px-2
                    py-1

                    text-[11px]
                    font-semibold
                    text-primary
                  "
                >
                  Vendidos: {soldQuantity}
                </span>

                <span
                  className="
                    rounded-full
                    bg-[#DC3545]/10

                    px-2
                    py-1

                    text-[11px]
                    font-semibold
                    text-[#DC3545]
                  "
                >
                  Acreditados: {returnedQuantity}
                </span>

                <span
                  className="
                    rounded-full
                    bg-[#3CA745]/10

                    px-2
                    py-1

                    text-[11px]
                    font-semibold
                    text-[#3CA745]
                  "
                >
                  Disponibles: {remainingQuantity}
                </span>
              </div>

              <div
                className="
                  mt-3

                  text-lg
                  font-bold
                  text-black

                  dark:text-white
                "
              >
                ${Number(product.unitPrice || 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* 🔹 Action */}
          <POSActionButton
            fullWidth
            variant="primary"
            disabled={!canBeReturned}
            onClick={() => setShowModal(true)}
          >
            Agregar crédito
          </POSActionButton>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Desktop */}
        {/* ===================================================== */}
        <div
          className="
            hidden

            w-full
            items-center
            gap-4

            px-4
            py-4

            lg:flex
          "
        >
          {/* 🔹 Image */}
          <div
            className="
              flex
              h-14
              w-14
              shrink-0
              items-center
              justify-center

              overflow-hidden

              rounded-xl
              border
              border-stroke

              bg-whiten

              dark:border-strokedark
              dark:bg-boxdark
            "
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.productName}
                className="
                  h-full
                  w-full
                  object-cover
                "
              />
            ) : (
              <span
                className="
                  text-[10px]
                  font-medium
                  text-body
                "
              >
                IMG
              </span>
            )}
          </div>

          {/* 🔹 Product */}
          <div className="min-w-0 flex-1">
            <h3
              className="
                truncate

                text-sm
                font-semibold
                text-black

                dark:text-white
              "
            >
              {product.productName}
            </h3>

            <p
              className="
                mt-1

                text-xs
                text-body
              "
            >
              ${Number(product.unitPrice || 0).toFixed(2)} por unidad
            </p>
          </div>

          {/* 🔹 Sold */}
          <div className="w-[120px] shrink-0 text-center">
            <span
              className="
                text-sm
                font-semibold
                text-black

                dark:text-white
              "
            >
              {soldQuantity}
            </span>
          </div>

          {/* 🔹 Returned */}
          <div className="w-[120px] shrink-0 text-center">
            <span
              className="
                text-sm
                font-semibold
                text-[#DC3545]
              "
            >
              {returnedQuantity}
            </span>
          </div>

          {/* 🔹 Remaining */}
          <div className="w-[120px] shrink-0 text-center">
            <span
              className="
                text-sm
                font-semibold
                text-[#3CA745]
              "
            >
              {remainingQuantity}
            </span>
          </div>

          {/* 🔹 Action */}
          <div
            className="
              flex
              w-[180px]
              shrink-0
              justify-end
            "
          >
            <POSActionButton
              variant="primary"
              disabled={!canBeReturned}
              onClick={() => setShowModal(true)}
            >
              Agregar crédito
            </POSActionButton>
          </div>
        </div>
      </motion.div>

      {/* 🔹 Modal */}
      <POSCreditQuantityModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmReturn}
        product={{
          productId: product.productId,

          productName: product.productName,

          quantity: soldQuantity,

          unitPrice: Number(product.unitPrice || 0),

          returnedQuantity,
        }}
      />
    </>
  );
};

export default POSCreditNoteProductListItem;
